import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Tooltip,
  message,
} from 'antd';
import { FormInstance } from 'antd/lib/form';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { createSyntheticApi } from '../../../api/apiKey';
import { fetchGroupsWithDescription } from '../../../api/groups';
import { APIKeyPayload } from '../../../constants/types';
import { supervisorpfUserGroup } from '../../../constants/userGroup';
import theme from '../../../styles/theme';
import { SHORT_DATE_TIME_FORMAT, disabledTime } from '../../../utils/date';
import { formatErrorMessage } from '../../../utils/errorMessage';
import { getParentOrigin } from '../../../utils/getKeycloakConfig';
import { ErrorMessage } from '../APIKeyInstance/style';
import {
  AddGroupDetails,
  CheckboxContainer,
  CounterExtension,
  FormItemSubLabel,
  Note,
  StyledCheckboxGroup,
  StyledCheckboxItem,
  StyledFormItem,
} from '../style';
import { checkDuplicateKeyName, createValidateKeyName } from './validators';

const { TextArea } = Input;

interface APIKeyFormValues
  extends Omit<APIKeyPayload, 'groups' | 'activeFrom' | 'expiresAt'> {
  keycloakGroups: string[];
  activeFrom: moment.Moment;
  expiresAt: moment.Moment;
}

const AddAPIKeyForm = () => {
  const history = useHistory();
  const intl = useIntl();
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const parentOrigin = getParentOrigin();

  const [groups, setGroups] = useState<any>([]);
  const [groupsError, setGroupsError] = useState<string | undefined>(undefined);
  const [isCreating, setIsCreating] = useState(false);

  // Create the validator with intl
  const validateKeyName = createValidateKeyName(intl);
  const checkDuplicateKeyNameValidator = checkDuplicateKeyName(intl);

  useEffect(() => {
    fetchGroupsWithDescription().then(groups => {
      const groupsListDetails = groups
        .filter(m => m.name !== supervisorpfUserGroup)
        .map(group => ({
          value: group.name,
          label: group.name,
          description: group.description,
        }));

      setGroups(groupsListDetails);
    });

    return () => {
      setGroups([]);
    };
  }, []);

  const disabledDate = (current: any) => {
    let disabledDates = false;

    if (current) {
      if (form.getFieldValue('activeFrom')) {
        disabledDates =
          current <= moment(form.getFieldValue('activeFrom')).endOf('day') ||
          current <= moment().startOf('day');
      } else {
        disabledDates = current <= moment().endOf('day');
      }
    }

    return disabledDates;
  };

  const validGroups = (groups: string[]) => {
    let isValid = true;

    if (groups.length > 0) {
      setGroupsError(undefined);
    } else {
      isValid = false;
      setGroupsError(
        intl.formatMessage({
          id: 'ApiKeys.form.validation.userGroups',
        }),
      );
    }

    return isValid;
  };

  const onGenerateAPIKey = async (apiKeyDetails: APIKeyFormValues) => {
    let groups: any = [];

    if (apiKeyDetails.keycloakGroups?.length > 0) {
      groups = apiKeyDetails.keycloakGroups;
    }

    if (validGroups(groups)) {
      setIsCreating(true);
      const payload: APIKeyPayload = {
        keyName: apiKeyDetails.keyName.trim(),
        keyDescription: apiKeyDetails.keyDescription.trim(),
        groups,
        activeFrom: apiKeyDetails.activeFrom.utc().format(),
        expiresAt: apiKeyDetails.expiresAt.utc().format(),
      };

      try {
        const { status, data } = await createSyntheticApi(payload);

        if (status === 'success') {
          if (parentOrigin && window.top && window.top !== window) {
            window.top.postMessage(
              {
                type: 'redirect',
                url: `/admin/apikeys?apikey=${data.apikey}`,
              },
              parentOrigin,
            );
          } else {
            history.push(`/apikeys?apikey=${data.apikey}`);
          }
        }
      } catch (error: any) {
        message.error(formatErrorMessage(error?.data?.errors[0]), 2);
      } finally {
        setIsCreating(false);
      }
    }
  };

  const backToAPIKeyPage = () => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/apikeys`,
        },
        parentOrigin,
      );
    } else {
      history.push('/apikeys');
    }
  };

  return (
    <>
      <Form
        layout="vertical"
        initialValues={{
          activeFrom: moment(),
          expiresAt: moment().add(1, 'years'),
        }}
        ref={formRef}
        form={form}
        onFinish={onGenerateAPIKey}
        data-testid="create-apikey-form"
      >
        <div style={{ position: 'relative' }}>
          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.form.label.keyName" />}
            name="keyName"
            validateTrigger={['onBlur', 'onChange']}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'ApiKeys.form.validation.keyName',
                }),
              },
              { validator: validateKeyName },
              {
                validator: checkDuplicateKeyNameValidator,
                validateTrigger: ['onBlur'],
              },
            ]}
          >
            <Input
              data-testid="create-apikey-name"
              maxLength={50}
              onChange={e => {
                const normalizedValue = e.target.value.replace(/\s+/g, ' ');

                if (normalizedValue !== e.target.value) {
                  form.setFieldsValue({ keyName: normalizedValue });
                }
              }}
            />
          </StyledFormItem>
          <StyledFormItem noStyle shouldUpdate dependencies={['keyName']}>
            {({ getFieldValue }) => (
              <CounterExtension>
                {getFieldValue('keyName')?.length || 0}/50 characters
              </CounterExtension>
            )}
          </StyledFormItem>
        </div>
        <div style={{ position: 'relative' }}>
          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.form.label.description" />}
            name="keyDescription"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'ApiKeys.form.validation.description',
                }),
              },
            ]}
          >
            <TextArea
              rows={3}
              showCount
              maxLength={200}
              data-testid="create-apikey-description"
            />
          </StyledFormItem>
          <CounterExtension>characters</CounterExtension>
        </div>

        <StyledFormItem
          style={{ marginBottom: '0' }}
          label={<FormattedMessage id="ApiKeys.form.label.userGroups" />}
          required
        >
          <FormItemSubLabel>
            <FormattedMessage id="ApiKeys.form.label.keycloakGroups" />
          </FormItemSubLabel>

          <CheckboxContainer style={{ height: '246px' }}>
            <StyledFormItem
              name="keycloakGroups"
              style={{ marginBottom: '0px' }}
              data-testid="create-apikey-keycloakgroups"
            >
              <StyledCheckboxGroup
                onChange={() => setGroupsError(undefined)}
                className="without-boarder"
              >
                {groups.map((group: any, index: number) => (
                  <StyledCheckboxItem key={index}>
                    <Checkbox
                      data-testid={`create-apikey-keycloakgroups-${index}`}
                      value={group.value}
                    >
                      {group.label}
                    </Checkbox>
                    <Tooltip title={group.description}>
                      <InfoCircleOutlined
                        style={{ color: theme.palette.primary.main }}
                      />
                    </Tooltip>
                  </StyledCheckboxItem>
                ))}
              </StyledCheckboxGroup>
            </StyledFormItem>
          </CheckboxContainer>
        </StyledFormItem>

        {groupsError && (
          <ErrorMessage>
            <span className="icon-wraper">
              <WarningOutlined data-testid="WarningOutlinedIcon" />
            </span>
            {groupsError}
          </ErrorMessage>
        )}

        <AddGroupDetails>
          <FormattedMessage id="ApiKeys.form.label.activePeriod" />
        </AddGroupDetails>

        <Row gutter={20}>
          <Col span={12}>
            <StyledFormItem
              label={<FormattedMessage id="ApiKeys.modal.label.activefrom" />}
              name="activeFrom"
              validateFirst
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'ApiKeys.GenerateApikeyModal.activeFrom.validation',
                  }),
                },
                {
                  validator: async (_rule, value) => {
                    const expiresAt = form.getFieldValue('expiresAt');
                    if (
                      expiresAt &&
                      new Date(value).getTime() > new Date(expiresAt).getTime()
                    ) {
                      throw new Error(
                        'Active date should be less than Expiry date',
                      );
                    }
                  },
                },
              ]}
            >
              <DatePicker
                data-testid="create-apikey-activedate"
                showTime
                className="date-picker"
                format={SHORT_DATE_TIME_FORMAT}
              />
            </StyledFormItem>
          </Col>
          <Col span={12}>
            <StyledFormItem
              label={<FormattedMessage id="ApiKeys.form.label.expiresAt" />}
              name="expiresAt"
              validateFirst
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'ApiKeys.GenerateApikeyModal.expiryFrom.validation',
                  }),
                },
                {
                  validator: async (_rule, value) => {
                    if (new Date(value).getTime() < new Date().getTime()) {
                      throw new Error(
                        'Expiry date should be a future date and time',
                      );
                    }
                  },
                },
              ]}
            >
              <DatePicker
                data-testid="create-apikey-expirydate"
                showTime
                className="date-picker"
                format={SHORT_DATE_TIME_FORMAT}
                disabledDate={disabledDate}
                disabledTime={disabledTime}
                onChange={() => {
                  form.validateFields(['activeFrom']);
                }}
              />
            </StyledFormItem>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Note>
              <span className="required">*</span>
              <FormattedMessage id="UserGroup.form.label.cumpulsoryFields" />
            </Note>
          </Col>
        </Row>
        <Row justify="center" style={{ marginTop: '30px' }}>
          <Button
            shape="round"
            size="large"
            style={{ marginRight: '10px' }}
            onClick={backToAPIKeyPage}
            type="default"
            data-testid="create-apikey-cancel"
          >
            <FormattedMessage id="Global.cancel" />
          </Button>
          <Button
            type="primary"
            shape="round"
            htmlType="submit"
            size="large"
            style={{ marginLeft: '10px' }}
            data-testid="create-apikey-proceed"
            disabled={isCreating}
          >
            <FormattedMessage id="ApiKeys.modal.btn.create" />
          </Button>
        </Row>
      </Form>
    </>
  );
};

export default AddAPIKeyForm;
